import React, { useState, useRef, useEffect } from "react";
import { Button, Form, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import InputCard from "../../../../../Components/Cards/InputCard";
import {
  getFuelBrandById,
  updateFuelBrand,
} from "../../../../../API/Admin/SystemConfiguration/FuelBrands";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const shortenFileName = (fileName, maxLength = 50) => {
  if (!fileName || fileName.length <= maxLength) return fileName;

  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex > 0 ? fileName.slice(dotIndex) : "";
  const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;

  return `${baseName.slice(0, maxLength - extension.length)}${extension}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const EditFuelBrand = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const initialAvatarRef = useRef(null);
  const location = useLocation();
  const brandId = location.state?.id;

  useEffect(() => {
    if (!brandId) return;

    const fetchBrand = async () => {
      try {
        const res = await getFuelBrandById(brandId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          form.setFieldsValue({
            brandName: data.brand_name,
            legalEntityName: data.legalentity_name,
          });
          const resolvedAvatar = resolveAvatarUrl(data.avatar);
          setAvatarPreview(resolvedAvatar);
          initialAvatarRef.current = resolvedAvatar;
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load fuel brand."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel brand."),
        );
      }
    };

    fetchBrand();
  }, [brandId, form]);

  const handleBack = () =>
    navigate("/system-configuration", {
      state: { activeTab: location.state?.from || "Fuel Brands" },
    });
  const handleButtonClick = () => fileInputRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvatarFile(null);
    setAvatarPreview(initialAvatarRef.current);
  };

  const brandInformationFields = [
    {
      name: "brandName",
      label: "Brand Name",
      type: "text",
      placeholder: "Enter Brand Name",
      rules: [{ required: true, message: "Brand name is required" }],
    },
    {
      name: "legalEntityName",
      label: "Legal Entity Name",
      type: "textarea",
      placeholder: "Enter Legal Entity Name",
      rules: [{ required: true, message: "Legal Entity Name is required" }],
    },
  ];

  const handleSubmit = async () => {
    if (!brandId) {
      message.error("Missing fuel brand id.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      message.error("Please complete the form before saving.");
      return;
    }

    const formData = new FormData();
    formData.append("brand_name", values.brandName);
    formData.append("legalentity_name", values.legalEntityName);
    if (avatarFile) {
      formData.append("avatar", avatarFile, shortenFileName(avatarFile.name));
    }

    setSubmitting(true);
    try {
      const res = await updateFuelBrand(brandId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || "Fuel Brand updated successfully!",
        );
        navigate("/system-configuration", {
          state: { activeTab: location.state?.from || "Fuel Brands" },
        });
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update fuel brand."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update fuel brand."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="edit-company add-fuel">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="title">Edit Fuel Brand</div>
      <Form form={form} layout="vertical">
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Change Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  form.getFieldValue("brandName")?.charAt(0).toUpperCase() ||
                  "?"
                )}
              </div>
            </div>
            <div className="profile-body" />
          </div>
        </div>

        <div className="input-cards-container">
          <InputCard title="Brand Name" fields={brandInformationFields} />
        </div>

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={submitting}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditFuelBrand;
